import React, { useState } from "react";
import { TimePicker, Checkbox, Switch, message, Button } from "antd";
import dayjs from "dayjs";
import { postRequest } from "../service/index";
import AttendanceRules, { sanitizeRelaxableSettings } from "./AttendanceRules";

const defaultSettings = {
  regDays: 7,
  applyDays: 3,
  applyPeriod: 'Month',
  isAttendanceRulesEnabled: true,
  isEarlyOutRulesEnabled: true,
  isRelaxableSettingsEnabled: false,
  lateRuleType: 'count',
  rule1: {
    lateCount: 3,
    lateMinutes: 15,
    latePeriod: 'Month',
    lateDeduction: 'half'
  },
  rule2: {
    lateMinutes: 15,
    lateTimeUnit: 'minute',
    lateTimeDeduction: '1'
  },
  earlyOutRuleType: 'count',
  earlyOutRule1: {
    earlyOutCount: 3,
    earlyOutMinutes: 15,
    earlyOutPeriod: 'Month',
    earlyOutDeduction: 'half'
  },
  earlyOutRule2: {
    earlyOutMinutes: 15,
    earlyOutTimeUnit: 'minute',
    earlyOutTimeDeduction: '1'
  },
  relaxable: {
    requiredHours: 9,
    period: 'Day',
    flexibleStartHours: 2,
    lateArrivalDeduction: 'quarter',
    relaxableRuleType: 'lateArrival'
  }
};

const ShiftModal = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("shift");
  const [shiftName, setShiftName] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [workingHours, setWorkingHours] = useState("");
  const [breakTimeEnabled, setBreakTimeEnabled] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [breakEndTime, setBreakEndTime] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsData, setSettingsData] = useState(defaultSettings);
  const [dayChange, setDayChange] = useState(false);

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const calculateWorkingHours = (start, end) => {
    if (!start || !end) return;
    const startMoment = dayjs(start);
    let endMoment = dayjs(end);
    
    // Check if shift spans across days
    const spansAcrossDays = endMoment.isBefore(startMoment);
    setDayChange(spansAcrossDays);
    
    if (spansAcrossDays) {
      endMoment = endMoment.add(1, 'day');
    }
    let diffHours = endMoment.diff(startMoment, 'hour', true);
    if (breakTimeEnabled && breakStartTime && breakEndTime) {
      const breakStart = dayjs(breakStartTime);
      let breakEnd = dayjs(breakEndTime);
      if (breakEnd.isBefore(breakStart)) {
        breakEnd = breakEnd.add(1, 'day');
      }
      const breakHours = breakEnd.diff(breakStart, 'hour', true);
      diffHours -= breakHours;
    }
    diffHours = Math.max(0, diffHours);
    setWorkingHours(diffHours.toFixed(1));
  };

  const handleStartTimeChange = (time) => {
    setStartTime(time);
    calculateWorkingHours(time, endTime);
  };

  const handleEndTimeChange = (time) => {
    setEndTime(time);
    calculateWorkingHours(startTime, time);
  };

  const handleBreakStartTimeChange = (time) => {
    setBreakStartTime(time);
    calculateWorkingHours(startTime, endTime);
  };

  const handleBreakEndTimeChange = (time) => {
    setBreakEndTime(time);
    calculateWorkingHours(startTime, endTime);
  };

  const handleBreakTimeToggle = (checked) => {
    setBreakTimeEnabled(checked);
    calculateWorkingHours(startTime, endTime);
  };

  function filterSettings(settings) {
    return {
      regDays: settings.regDays,
      applyDays: settings.applyDays,
      applyPeriod: settings.applyPeriod,
      isAttendanceRulesEnabled: settings.isAttendanceRulesEnabled,
      lateRuleType: settings.lateRuleType,
      rule1: settings.rule1,
      rule2: settings.rule2,
      isEarlyOutRulesEnabled: settings.isEarlyOutRulesEnabled,
      earlyOutRuleType: settings.earlyOutRuleType,
      earlyOutRule1: settings.earlyOutRule1,
      earlyOutRule2: settings.earlyOutRule2,
      isRelaxableSettingsEnabled: settings.isRelaxableSettingsEnabled,
      relaxable: sanitizeRelaxableSettings(settings.relaxable),
    };
  }

  const handleSubmit = async () => {
    try {
      setLoading(true);
      // Deep copy settingsData to avoid mutating state
      const settingsCopy = JSON.parse(JSON.stringify(settingsData));
      // Convert rule2.lateMinutes to minutes if lateTimeUnit is 'hour'
      if (
        settingsCopy.rule2 &&
        settingsCopy.rule2.lateTimeUnit === 'hour' &&
        settingsCopy.rule2.lateMinutes
      ) {
        const hours = parseFloat(settingsCopy.rule2.lateMinutes);
        if (!isNaN(hours)) {
          settingsCopy.rule2.lateMinutes = hours * 60;
          settingsCopy.rule2.lateTimeUnit = 'minute'; // Normalize unit for backend
        }
      }
      
      // Convert earlyOutRule2.earlyOutMinutes to minutes if earlyOutTimeUnit is 'hour'
      if (
        settingsCopy.earlyOutRule2 &&
        settingsCopy.earlyOutRule2.earlyOutTimeUnit === 'hour' &&
        settingsCopy.earlyOutRule2.earlyOutMinutes
      ) {
        const hours = parseFloat(settingsCopy.earlyOutRule2.earlyOutMinutes);
        if (!isNaN(hours)) {
          settingsCopy.earlyOutRule2.earlyOutMinutes = hours * 60;
          settingsCopy.earlyOutRule2.earlyOutTimeUnit = 'minute'; // Normalize unit for backend
        }
      }
      const data = {
        shiftName,
        workingHours: parseFloat(workingHours),
        startTime: startTime?.format("HH:mm"),
        endTime: endTime?.format("HH:mm"),
        breakTimeStart: breakTimeEnabled ? breakStartTime?.format("HH:mm") : null,
        breakTimeEnd: breakTimeEnabled ? breakEndTime?.format("HH:mm") : null,
        workingDays: selectedDays,
        dayChange: dayChange,
        settings: filterSettings(settingsCopy)
      };
      const response = await postRequest("/api/companies/shifts", data);
      if (response?.message === "Shift created successfully") {
        message.success("Shift created successfully!");
        onClose();
      } else {
        message.error(response?.error || "Failed to create shift. Please try again.");
      }
    } catch (error) {
      console.error("Error creating shift:", error);
      message.error(error?.message || "Failed to create shift. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (day, checked) => {
    if (checked) {
      setSelectedDays([...selectedDays, day]);
    } else {
      setSelectedDays(selectedDays.filter(d => d !== day));
    }
  };

  return (
    <div className="h-[70vh] overflow-y-auto pr-3">
      <div className="border-b border-gray-200 flex space-x-8 mb-4">
        <button
          className={`py-2 px-1 text-md font-medium ${
            activeTab === "shift"
              ? "border-b-2 border-indigo-700 text-indigo-700"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("shift")}
        >
          Shift
        </button>
        <button
          className={`py-2 px-1 text-md font-medium ${
            activeTab === "settings"
              ? "border-b-2 border-indigo-700 text-indigo-700"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("settings")}
        >
          Settings
        </button>
      </div>
      {activeTab === "shift" && (
        <>
          <div className="flex flex-col gap-3 ">
      <div className="flex flex-col gap-1">
        <label htmlFor="shift-name" className="text-md">
          Shift Name
        </label>
        <input
          type="text"
          id="shift-name"
          className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md"
          placeholder="i.e Morning Shift"
          value={shiftName}
          onChange={(e) => setShiftName(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-12 gap-4">
        <div className="flex flex-col col-span-6 gap-2">
          <label htmlFor="shift-start" className="text-md">
            Start Time(e.g 09:00)
          </label>
          <TimePicker 
            id="shift-start" 
            onChange={handleStartTimeChange} 
            format="HH:mm" 
          />
        </div>
        <div className="flex flex-col gap-2 col-span-6">
          <label htmlFor="shift-end" className="text-md">
            End Time (e.g 17:00)
          </label>
          <TimePicker 
            id="shift-end" 
            onChange={handleEndTimeChange} 
            format="HH:mm" 
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <label className="text-md">Break Time</label>
          <Switch 
            checked={breakTimeEnabled}
            onChange={handleBreakTimeToggle}
          />
        </div>
        {breakTimeEnabled && (
          <div className="grid grid-cols-12 gap-4">
            <div className="flex flex-col col-span-6 gap-2">
              <label htmlFor="break-start" className="text-md">
                Break Start Time
              </label>
              <TimePicker 
                id="break-start" 
                onChange={handleBreakStartTimeChange} 
                format="HH:mm" 
              />
            </div>
            <div className="flex flex-col gap-2 col-span-6">
              <label htmlFor="break-end" className="text-md">
                Break End Time
              </label>
              <TimePicker 
                id="break-end" 
                onChange={handleBreakEndTimeChange} 
                format="HH:mm" 
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="working-hours" className="text-md">
          Working Hours
        </label>
        <input
          type="text"
          id="working-hours"
          className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md"
          value={workingHours}
          readOnly
          placeholder="Auto-calculated working hours"
        />
      </div>
      
      {startTime && endTime && (
        <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
          <span className="text-sm font-medium">Shift Type:</span>
          <span className={`text-sm px-2 py-1 rounded-full ${
            dayChange 
              ? 'bg-orange-100 text-orange-700' 
              : 'bg-green-100 text-green-700'
          }`}>
            {dayChange ? 'Overnight Shift (spans across days)' : 'Same Day Shift'}
          </span>
        </div>
      )}
           
      <h4 className="font-semibold">Working Days</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-2 gap-y-2">
        {days.map((day) => (
                <div className="flex items-center gap-3 text-md" key={day}>
            <Checkbox 
              id={day}
              onChange={(e) => handleDayChange(day, e.target.checked)}
              checked={selectedDays.includes(day)}
            >
              {day}
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
    </>
      )}
      {activeTab === "settings" && (
        <div>
          <AttendanceRules settingsData={settingsData} setSettingsData={setSettingsData} />
        </div>
      )}
      {/* Move the button group here so it's always visible */}
      <div className="flex justify-end gap-2 mt-4">
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            loading={loading}
            className="bg-primary hover:bg-primary/80"
          >
             {loading ? "Creating..." : "Create Shift"}
          </Button>
        </div>
    </div>
  );
};

export default ShiftModal;
